import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FiEye, FiChevronDown } from 'react-icons/fi'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from 'recharts'
import MetricCard from '@/components/ui/MetricCard'
import FinanceInvoicesTableCard, { type InvoiceTableRow } from '@/components/finance/FinanceInvoicesTableCard'
import InvoiceDetailsModal from '@/pages/finance/InvoiceDetailsModal'
import PdfViewerModal from '@/pages/finance/PdfViewerModal'
import { ROUTES } from '@/constants/routes'
import { FINANCE_TABS } from '@/constants/financeTabs'
import { getXeroBillPdfUrl } from '@/services/api/xero'
import {
  getFinanceOverview,
  getInvoices,
  getPurchases,
  getProductsServices,
  invoiceToTableRow,
  purchaseToTableRow,
  productToTableRow,
  type FinanceMetricTrends,
} from '@/services/api/finance'
import {
  prepareSparkline,
  sparklineColor,
  formatMonthOnMonthTrend,
  buildMetricTrends,
} from '@/utils/metricSparkline'

const CLIENT_REVENUE_COLORS = ['#082B09', '#6B806B', '#B5C0B6', '#E7EAE7', '#13AB5B', '#2C71F6', '#EEC233', '#E93323', '#69697A', '#212126']

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const CHART_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec',
] as const

const CHART_COLORS = {
  profit: '#13AB5B',
  loss: '#E93323',
  netIncomeCurrent: '#2C71F6',
  netIncomePrior: '#F4A8A8',
} as const

const PNL_CHART_PERIOD_OPTIONS = [{ label: 'Month', value: 'month' }] as const

function formatChartYTick(value: number): string {
  if (value === 0) return '0'
  return `${value / 1000}K`
}

/** Y-axis max rounded to 50K steps (Figma: 0, 50K, 100K, …). */
function computePositiveChartDomain(values: number[]): [number, number] {
  const max = values.length ? Math.max(...values, 0) : 0
  if (max <= 0) return [0, 200000]
  const step = 50000
  const ceiling = Math.ceil((max * 1.08) / step) * step
  return [0, Math.max(ceiling, step)]
}

function formatGrowthPercent(percent: number): string {
  const abs = Math.abs(percent)
  if (abs >= 999) return '>999%'
  return `${abs.toFixed(1)}%`
}

/** Jan–current month slice for year-series charts (matches Figma monthly axis). */
function filterYearToDateChartSeries<T extends { month: string }>(data: T[]): T[] {
  const endIdx = new Date().getMonth()
  const allowed = new Set(CHART_MONTHS.slice(0, endIdx + 1))
  return data.filter((d) => allowed.has(d.month as (typeof CHART_MONTHS)[number]))
}

function getChartPeriodLabel(): string {
  const now = new Date()
  const start = new Date(now.getFullYear(), 0, 1)
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  return `${fmt(start)} - ${fmt(now)}`
}

const chartLegendFormatter = (value: string, entry: { color?: string }) => (
  <span
    style={{
      fontFamily: 'Lexend Deca',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#69697A',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    }}
  >
    <span
      style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: entry.color,
        display: 'inline-block',
      }}
    />
    {value}
  </span>
)

const TIMEFRAME_OPTIONS = [
  { label: 'Month', value: 'month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Week', value: 'week' },
  { label: 'Year', value: 'year' },
] as const

const FinanceOverview = () => {
  const location = useLocation()
  // Default to year so top KPIs match the P&L / net-income charts (month can be empty early in Nov).
  const [timeframe, setTimeframe] = useState<string>('year')
  const [dateFilter, setDateFilter] = useState('Any Date')
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [overviewError, setOverviewError] = useState<string | null>(null)
  const [overview, setOverview] = useState<{
    totalRevenue: number
    pendingPayments: number
    paidInvoices: number
    overdueAmount: number
    profitLoss: { month: string; loss: number; profit: number }[]
    netIncome: { month: string; current: number; prior: number }[]
    clientRevenue: { name: string; value: number }[]
    metricTrends?: {
      totalRevenue: number[]
      pendingPayments: number[]
      paidInvoices: number[]
      overdueAmount: number[]
    }
  } | null>(null)
  const [invoicesData, setInvoicesData] = useState<ReturnType<typeof invoiceToTableRow>[]>([])
  const [purchasesData, setPurchasesData] = useState<ReturnType<typeof purchaseToTableRow>[]>([])
  const [productsData, setProductsData] = useState<ReturnType<typeof productToTableRow>[]>([])
  const [tablesLoading, setTablesLoading] = useState(true)
  const [invoicesSearch, setInvoicesSearch] = useState('')
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceTableRow | null>(null)
  const [isInvoiceDetailsOpen, setIsInvoiceDetailsOpen] = useState(false)
  const [invoicePdfOpen, setInvoicePdfOpen] = useState(false)
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null)
  const [invoicePdfTitle, setInvoicePdfTitle] = useState('Invoice')
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(false)
  const [invoicePdfError, setInvoicePdfError] = useState<string | null>(null)
  const [chartSeriesLoading, setChartSeriesLoading] = useState(true)
  const [chartSeries, setChartSeries] = useState<{
    profitLoss: { month: string; loss: number; profit: number }[]
    netIncome: { month: string; current: number; prior: number }[]
    metricTrends?: FinanceMetricTrends
  }>({ profitLoss: [], netIncome: [] })

  const zeroOverview = {
    totalRevenue: 0,
    pendingPayments: 0,
    paidInvoices: 0,
    overdueAmount: 0,
    profitLoss: [] as { month: string; loss: number; profit: number }[],
    netIncome: [] as { month: string; current: number; prior: number }[],
    clientRevenue: [] as { name: string; value: number }[],
    metricTrends: {
      totalRevenue: [] as number[],
      pendingPayments: [] as number[],
      paidInvoices: [] as number[],
      overdueAmount: [] as number[],
    },
  }

  useEffect(() => {
    let cancelled = false
    setOverviewLoading(true)
    setOverviewError(null)
    getFinanceOverview({ timeframe })
      .then((data) => {
        if (!cancelled) setOverview(data)
      })
      .catch((err) => {
        if (!cancelled) {
          setOverview(zeroOverview)
          setOverviewError(err?.message || 'Failed to load overview')
        }
      })
      .finally(() => {
        if (!cancelled) setOverviewLoading(false)
      })
    return () => { cancelled = true }
  }, [timeframe])

  // Monthly chart series (year API) — matches Figma Jan–Jun style axis
  useEffect(() => {
    let cancelled = false
    setChartSeriesLoading(true)
    getFinanceOverview({ timeframe: 'year' })
      .then((data) => {
        if (!cancelled) {
          setChartSeries({
            profitLoss: data.profitLoss ?? [],
            netIncome: data.netIncome ?? [],
            metricTrends: data.metricTrends,
          })
        }
      })
      .catch(() => {
        if (!cancelled) setChartSeries({ profitLoss: [], netIncome: [] })
      })
      .finally(() => {
        if (!cancelled) setChartSeriesLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const getDateRangeForTimeframe = (tf: string): { startDate?: string; endDate?: string } => {
    const now = new Date()
    const toISO = (d: Date) => d.toISOString().slice(0, 10)
    switch (tf) {
      case 'month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        return { startDate: toISO(start), endDate: toISO(end) }
      }
      case 'last_month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const end = new Date(now.getFullYear(), now.getMonth(), 0)
        return { startDate: toISO(start), endDate: toISO(end) }
      }
      case 'week': {
        const end = new Date(now)
        const start = new Date(now)
        start.setDate(start.getDate() - 6)
        return { startDate: toISO(start), endDate: toISO(end) }
      }
      case 'year': {
        const start = new Date(now.getFullYear(), 0, 1)
        const end = new Date(now.getFullYear(), 11, 31)
        return { startDate: toISO(start), endDate: toISO(end) }
      }
      default:
        return {}
    }
  }

  const getDateRangeForPurchasesFilter = (filter: string): { startDate?: string; endDate?: string } => {
    const now = new Date()
    const toISO = (d: Date) => d.toISOString().slice(0, 10)
    switch (filter) {
      case 'This Week': {
        const end = new Date(now)
        const start = new Date(now)
        start.setDate(start.getDate() - 6)
        return { startDate: toISO(start), endDate: toISO(end) }
      }
      case 'This Month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        return { startDate: toISO(start), endDate: toISO(end) }
      }
      case 'This Year': {
        const start = new Date(now.getFullYear(), 0, 1)
        const end = new Date(now.getFullYear(), 11, 31)
        return { startDate: toISO(start), endDate: toISO(end) }
      }
      default:
        return {}
    }
  }

  useEffect(() => {
    let cancelled = false
    setTablesLoading(true)
    const { startDate: invStart, endDate: invEnd } = getDateRangeForTimeframe(timeframe)
    const { startDate: purStart, endDate: purEnd } = getDateRangeForPurchasesFilter(dateFilter)
    const invParams = { page: 1, limit: 5, ...(invStart && { startDate: invStart }), ...(invEnd && { endDate: invEnd }) }
    const purParams = { page: 1, limit: 5, ...(purStart && { startDate: purStart }), ...(purEnd && { endDate: purEnd }) }
    Promise.all([
      getInvoices(invParams),
      getPurchases(purParams),
      getProductsServices({ page: 1, limit: 6 }),
    ])
      .then(([inv, pur, prod]) => {
        if (cancelled) return
        setInvoicesData((inv.invoices || []).map(invoiceToTableRow))
        setPurchasesData((pur.purchases || []).map(purchaseToTableRow))
        setProductsData((prod.products || []).map(productToTableRow))
      })
      .catch(() => {
        if (!cancelled) {
          setInvoicesData([])
          setPurchasesData([])
          setProductsData([])
        }
      })
      .finally(() => {
        if (!cancelled) setTablesLoading(false)
      })
    return () => { cancelled = true }
  }, [timeframe, dateFilter])

  const safeOverview = overview ?? zeroOverview
  const metricTrends = buildMetricTrends(
    chartSeries.metricTrends ?? safeOverview.metricTrends,
    chartSeries.profitLoss,
    {
      totalRevenue: safeOverview.totalRevenue,
      paidInvoices: safeOverview.paidInvoices,
      pendingPayments: safeOverview.pendingPayments,
      overdueAmount: safeOverview.overdueAmount,
    }
  )
  const metricsSparklinesReady = !chartSeriesLoading
  const profitLossData = filterYearToDateChartSeries(chartSeries.profitLoss)
  const profitLossChartData =
    profitLossData.length === 0
      ? [{ month: '—', loss: 0, profit: 0 }]
      : profitLossData.length === 1
        ? [{ month: '', loss: 0, profit: 0 }, ...profitLossData]
        : profitLossData
  const netIncomeData = filterYearToDateChartSeries(chartSeries.netIncome)
  const clientRevenueData = safeOverview.clientRevenue.map((item, i) => ({
    ...item,
    color: CLIENT_REVENUE_COLORS[i % CLIENT_REVENUE_COLORS.length],
  }))

  const profitLossHeadlineTotal = profitLossData.reduce((s, d) => s + (d.profit ?? 0), 0)
  const chartPeriodLabel = getChartPeriodLabel()
  const netIncomeCurrentTotal = netIncomeData.reduce((s, d) => s + (d.current ?? 0), 0)
  const netIncomePriorTotal = netIncomeData.reduce((s, d) => s + (d.prior ?? 0), 0)
  const pnlChartLoading = chartSeriesLoading || overviewLoading
  const netIncomeGrowthPercent =
    netIncomePriorTotal === 0
      ? netIncomeCurrentTotal > 0
        ? 100
        : 0
      : ((netIncomeCurrentTotal - netIncomePriorTotal) / Math.abs(netIncomePriorTotal)) * 100
  const profitLossChartValues = profitLossData.flatMap((d) => [d.profit ?? 0, d.loss ?? 0])
  const profitLossYDomain = computePositiveChartDomain(profitLossChartValues)
  const netIncomeValues = netIncomeData.flatMap((d) => [d.current ?? 0, d.prior ?? 0])
  const netIncomeHasNegative = netIncomeValues.some((v) => v < 0)
  const netIncomeYDomain: [number, number] = netIncomeHasNegative
    ? (() => {
        const absMax = netIncomeValues.length
          ? Math.max(...netIncomeValues.map((v) => Math.abs(v)), 1)
          : 1000
        const padded = absMax * 1.15
        return [-padded, padded] as [number, number]
      })()
    : computePositiveChartDomain(netIncomeValues)
  const handleInvoicesExport = () => {
    const query = invoicesSearch.trim().toLowerCase()
    const rows = (query
      ? invoicesData.filter(
          (invoice) =>
            (invoice.number && invoice.number.toLowerCase().includes(query)) ||
            (invoice.ref && invoice.ref.toLowerCase().includes(query)) ||
            (invoice.status && invoice.status.toLowerCase().includes(query))
        )
      : invoicesData
    ).map((inv) => [inv.number, inv.ref, inv.status])
    const csv = [['Number', 'Ref', 'Status'], ...rows].map((r) => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'invoices-overview.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleViewInvoice = (invoice: InvoiceTableRow) => {
    setSelectedInvoice(invoice)
    setIsInvoiceDetailsOpen(true)
  }

  const handleDownloadInvoice = async (invoice: InvoiceTableRow) => {
    if (!invoice.xeroInvoiceId) {
      setInvoicePdfTitle(`Invoice ${invoice.number}`)
      setInvoicePdfError('PDF is only available for invoices synced from Xero.')
      setInvoicePdfUrl(null)
      setInvoicePdfOpen(true)
      return
    }
    setInvoicePdfTitle(`Invoice ${invoice.number}`)
    setInvoicePdfError(null)
    setInvoicePdfUrl(null)
    setInvoicePdfLoading(true)
    setInvoicePdfOpen(true)
    try {
      const url = await getXeroBillPdfUrl(invoice.xeroInvoiceId)
      setInvoicePdfUrl(url)
    } catch {
      setInvoicePdfError('Could not load invoice PDF.')
    } finally {
      setInvoicePdfLoading(false)
    }
  }

  const handleNetIncomeExport = () => {
    const headers = ['Month', 'Current', 'Prior']
    const rows = netIncomeData.map((d) => [
      d.month,
      String(d.current ?? 0),
      String(d.prior ?? 0),
    ])
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `net-income-${timeframe}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', textColor: '#13AB5B', bgColor: '#E8F7EF' }
      case 'Pending':
        return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', textColor: '#EEC233', bgColor: '#FEF9EB' }
      case 'Overdue':
        return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', textColor: '#E93323', bgColor: '#FEE2E2' }
      default:
        return { text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', textColor: '#69697A', bgColor: '#F3F4F6' }
    }
  }

  const tabs = FINANCE_TABS

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div>
        {/* First Row: Finance title and Breadcrumbs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126',
            }}
          >
            Finance
          </h1>
          
          {/* Breadcrumbs on the right of Finance title */}
          <span
            className="text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: 'clamp(11px, 2.5vw, 12px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Overview</span>
          </span>
        </div>

        {/* Second Row: Tabs and Controls on the same level */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Tabs - full width so all tabs (including Business Analytics) are visible */}
          <div 
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '4px',
              gap: '0px'
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: isActive ? '#FFFFFF' : '#69697A',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>

          {/* Timeframe selector */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0" style={{ height: '40px' }}>
            <div className="relative">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="px-3 sm:px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none text-sm sm:text-base"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  lineHeight: '20px',
                  letterSpacing: '0%',
                  color: '#050521',
                  height: '40px',
                  minWidth: '120px',
                }}
              >
                {TIMEFRAME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <FiChevronDown 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                style={{
                  width: '16px',
                  height: '16px',
                  opacity: 0.8,
                  color: '#050521'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          title="Total Revenue"
          value={overviewLoading ? '…' : formatCurrency(safeOverview.totalRevenue)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.totalRevenue)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.totalRevenue)}
          graphColor={sparklineColor(metricTrends.totalRevenue)}
          icon={
            <img 
              src="/images/dollar-circle.svg" 
              alt="Dollar" 
              className="w-5 h-5" 
              style={{
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(103deg) brightness(95%) contrast(85%)'
              }}
            />
          }
          iconBgColor=""
          iconBgColorCustom="#E8F7EF"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Pending Payments"
          value={overviewLoading ? '…' : formatCurrency(safeOverview.pendingPayments)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.pendingPayments)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.pendingPayments)}
          graphColor={sparklineColor(metricTrends.pendingPayments, true)}
          icon={<img src="/images/empty-wallet-time.svg" alt="Pending Payments" className="w-5 h-5" />}
          iconBgColor=""
          iconBgColorCustom="#FEF9EB"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Paid Invoices"
          value={overviewLoading ? '…' : formatCurrency(safeOverview.paidInvoices)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.paidInvoices)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.paidInvoices)}
          graphColor={sparklineColor(metricTrends.paidInvoices)}
          icon={<img src="/images/receipt-text.svg" alt="Paid Invoices" className="w-5 h-5" />}
          iconBgColor=""
          iconBgColorCustom="#EAF1FF"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Overdue Amount"
          value={overviewLoading ? '…' : formatCurrency(safeOverview.overdueAmount)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.overdueAmount)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.overdueAmount)}
          graphColor={sparklineColor(metricTrends.overdueAmount, true)}
          icon={<img src="/images/coin.svg" alt="Overdue Amount" className="w-5 h-5" />}
          iconBgColor=""
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%' }}
        />
      </div>
      {overviewError && (
        <p className="text-sm text-amber-600" style={{ fontFamily: 'Lexend Deca' }}>
          Couldn&apos;t load latest data. Showing ZAR 0 for all metrics.
        </p>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Profit & Loss Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Profit & Loss
            </h3>
            <div className="relative flex items-center">
              <img 
                src="/images/calendar.svg" 
                alt="Calendar" 
                className="absolute left-3 pointer-events-none"
                style={{ width: '18px', height: '18px', opacity: 1 }}
              />
              <select
                defaultValue="month"
                className="pl-10 pr-8 py-1 border border-gray-300 rounded text-sm appearance-none bg-white"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0%',
                  textAlign: 'right',
                  color: '#212126',
                  minWidth: '110px',
                }}
                aria-label="Profit and loss chart period"
              >
                {PNL_CHART_PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <FiChevronDown 
                className="absolute right-3 pointer-events-none"
                style={{ width: '16px', height: '16px', opacity: 1, color: '#212126' }}
              />
            </div>
          </div>
          <div className="mb-4">
            <p
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '32px',
                letterSpacing: '0%',
                color: '#69697A',
              }}
            >
              {pnlChartLoading ? '…' : formatCurrency(profitLossHeadlineTotal)}
            </p>
            {!pnlChartLoading && profitLossData.length > 0 ? (
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '18px',
                  marginTop: '4px',
                }}
              >
                <span style={{ color: netIncomeGrowthPercent >= 0 ? '#13AB5B' : '#E93323' }}>
                  {netIncomeGrowthPercent >= 0 ? '↑' : '↓'}{' '}
                  {netIncomePriorTotal !== 0
                    ? formatGrowthPercent(netIncomeGrowthPercent)
                    : netIncomeCurrentTotal > 0
                      ? '100%'
                      : '0%'}
                </span>
                <span style={{ color: '#69697A' }}> from {chartPeriodLabel}</span>
              </p>
            ) : (
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  color: '#69697A',
                  marginTop: '4px',
                }}
              >
                {!pnlChartLoading
                  ? 'No data for this period. Sync from Xero (Settings → Integrations) to see Profit & Loss.'
                  : ''}
              </p>
            )}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart 
              data={profitLossChartData} 
              margin={{ left: 16, right: 20, top: 10, bottom: 0 }}
            >
              <defs>
                <linearGradient id="financePnlLoss" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.loss} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={CHART_COLORS.loss} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="financePnlProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.profit} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={CHART_COLORS.profit} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="#E5E7EB"
                strokeDasharray="4 4"
                horizontal={true}
                vertical={false}
              />
              <XAxis 
                dataKey="month" 
                tick={{
                  fill: '#050521',
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                }}
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  textAlign: 'center',
                }}
                axisLine={{
                  stroke: '#E9E9EA',
                  strokeWidth: 1,
                }}
                tickLine={false}
                padding={{ left: 10, right: 10 }}
                interval={0}
                allowDataOverflow={false}
              />
              <YAxis 
                tick={{
                  fill: '#69697A',
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                }}
                tickFormatter={formatChartYTick}
                tickMargin={8}
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                }}
                axisLine={false}
                tickLine={false}
                domain={profitLossYDomain}
                allowDecimals={false}
                width={56}
                ticks={
                  profitLossYDomain[1] > 0
                    ? Array.from(
                        { length: profitLossYDomain[1] / 50000 + 1 },
                        (_, i) => i * 50000
                      )
                    : undefined
                }
              />
              <Tooltip
                formatter={(value: number | string, name: string) => [
                  formatCurrency(Number(value)),
                  name,
                ]}
                itemSorter={(item) => (item.name === 'Profit' ? -1 : 1)}
                contentStyle={{ fontFamily: 'Lexend Deca', fontSize: 12 }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#69697A',
                  paddingTop: 12,
                }}
                iconSize={0}
                formatter={chartLegendFormatter}
              />
              <Area
                type="monotone"
                dataKey="loss"
                stroke={CHART_COLORS.loss}
                strokeWidth={2}
                fill="url(#financePnlLoss)"
                fillOpacity={1}
                name="Loss"
                isAnimationActive={false}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="profit"
                stroke={CHART_COLORS.profit}
                strokeWidth={2}
                fill="url(#financePnlProfit)"
                fillOpacity={1}
                name="Profit"
                isAnimationActive={false}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Net Income Chart – clustered columns, current vs prior around zero baseline */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Net Income
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <img 
                  src="/images/calendar.svg" 
                  alt="Calendar" 
                  className="absolute left-3 pointer-events-none"
                  style={{ width: '18px', height: '18px', opacity: 1 }}
                />
                <select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className="pl-10 pr-8 py-1 border border-gray-300 rounded text-sm appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    textAlign: 'right',
                    color: '#212126',
                  }}
                >
                  {TIMEFRAME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 pointer-events-none"
                  style={{ width: '16px', height: '16px', opacity: 1, color: '#212126' }}
                />
              </div>
              <button
                type="button"
                onClick={handleNetIncomeExport}
                disabled={pnlChartLoading || netIncomeData.length === 0}
                className="flex items-center justify-center gap-2 font-medium rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
                style={{
                  height: '32px',
                  color: '#FFFFFF',
                  backgroundColor: '#212126',
                  border: '1px solid #212126',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontFamily: 'Lexend Deca',
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                Export
              </button>
            </div>
          </div>
          <div className="mb-4 flex flex-wrap items-start gap-8">
            <div className="flex flex-col min-w-[160px]">
              <div className="flex flex-wrap items-baseline gap-2">
                <p
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '20px',
                    lineHeight: '32px',
                    letterSpacing: '0%',
                    color: '#212126',
                  }}
                >
                  {pnlChartLoading ? '…' : formatCurrency(netIncomeCurrentTotal)}
                </p>
                {!pnlChartLoading && netIncomePriorTotal !== 0 && (
                  <span
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 500,
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: netIncomeGrowthPercent >= 0 ? '#13AB5B' : '#E93323',
                    }}
                  >
                    {netIncomeGrowthPercent >= 0 ? 'Up' : 'Down'}{' '}
                    {formatGrowthPercent(netIncomeGrowthPercent)}
                  </span>
                )}
              </div>
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0%',
                  color: '#69697A',
                }}
              >
                Current period
              </p>
            </div>
            <div className="flex flex-col min-w-[160px]">
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '20px',
                  lineHeight: '32px',
                  letterSpacing: '0%',
                  color: netIncomePriorTotal < 0 ? '#E93323' : '#212126',
                }}
              >
                {pnlChartLoading ? '…' : formatCurrency(netIncomePriorTotal)}
              </p>
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  color: '#69697A',
                }}
              >
                Prior period
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={netIncomeData.length ? netIncomeData : [{ month: '—', current: 0, prior: 0 }]}
              margin={{ left: 16, right: 20, top: 10, bottom: 0 }}
              barCategoryGap="28%"
              barGap={6}
            >
              <CartesianGrid
                stroke="#E5E7EB"
                strokeDasharray="4 4"
                horizontal={true}
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tick={{
                  fill: '#050521',
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                }}
                axisLine={{
                  stroke: '#E9E9EA',
                  strokeWidth: 1,
                }}
                tickLine={false}
                padding={{ left: 10, right: 10 }}
                interval={0}
              />
              <YAxis
                tick={{
                  fill: '#69697A',
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                }}
                tickFormatter={formatChartYTick}
                tickMargin={8}
                axisLine={false}
                tickLine={false}
                domain={netIncomeYDomain}
                allowDecimals={false}
                width={56}
                ticks={
                  !netIncomeHasNegative && netIncomeYDomain[1] > 0
                    ? Array.from(
                        { length: netIncomeYDomain[1] / 50000 + 1 },
                        (_, i) => i * 50000
                      )
                    : undefined
                }
              />
              {netIncomeHasNegative && (
                <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
              )}
              <Tooltip
                formatter={(value: number | string, name: string) => [
                  formatCurrency(Number(value)),
                  name,
                ]}
                contentStyle={{ fontFamily: 'Lexend Deca', fontSize: 12 }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#69697A',
                  paddingTop: 12,
                }}
                iconSize={0}
                formatter={chartLegendFormatter}
              />
              <Bar
                dataKey="current"
                fill={CHART_COLORS.netIncomeCurrent}
                name="Current"
                barSize={16}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="prior"
                fill={CHART_COLORS.netIncomePrior}
                name="Prior"
                barSize={16}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Invoices and Purchases Overview Row - 2 cards on same level */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <FinanceInvoicesTableCard
          invoices={invoicesData}
          loading={tablesLoading}
          search={invoicesSearch}
          onSearchChange={setInvoicesSearch}
          viewAllHref={ROUTES.FINANCE_INVOICES}
          onExport={handleInvoicesExport}
          onViewInvoice={handleViewInvoice}
          onDownloadInvoice={handleDownloadInvoice}
        />

        {/* Purchases Overview Table */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
            <h3
              className="text-sm sm:text-base"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: 'clamp(13px, 3vw, 14px)',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Purchases Overview
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex items-center flex-shrink">
                <img 
                  src="/images/calendar.svg" 
                  alt="Calendar" 
                  className="absolute left-3 pointer-events-none z-10"
                  style={{ width: '18px', height: '18px', opacity: 1 }}
                />
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="pl-10 pr-8 py-1 border border-gray-300 rounded text-sm appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: 'clamp(12px, 3vw, 14px)',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    textAlign: 'right',
                    color: '#212126',
                    width: '100%',
                    maxWidth: '140px',
                    height: '34px',
                    paddingLeft: '44px',
                  }}
                >
                  <option>Any Date</option>
                  <option>This Week</option>
                  <option>This Month</option>
                  <option>This Year</option>
                </select>
                <FiChevronDown 
                  className="absolute right-3 pointer-events-none"
                  style={{ width: '16px', height: '16px', opacity: 1, color: '#212126', top: '50%', transform: 'translateY(-50%)' }}
                />
              </div>
              <Link
                to={ROUTES.FINANCE_PURCHASES}
                className="flex-shrink-0 flex items-center justify-center"
                style={{
                  width: '72px',
                  height: '34px',
                  borderRadius: '6px',
                  borderWidth: '1px',
                  border: '1px solid #2C71F6',
                  padding: '8px 12px',
                  backgroundColor: '#FFFFFF',
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: 'clamp(11px, 2.5vw, 12px)',
                  lineHeight: '20px',
                  letterSpacing: '0%',
                  color: '#2C71F6',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                View all
              </Link>
            </div>
          </div>
          <div className="overflow-x-auto -mx-4 sm:mx-0" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #E9E9EA' }}>
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E9E9EA' }}>
                  <th 
                    className="text-left py-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                      borderTopLeftRadius: '8px',
                    }}
                  >
                    Number
                  </th>
                  <th 
                    className="text-left py-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Ref
                  </th>
                  <th 
                    className="text-left py-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Status
                  </th>
                  <th 
                    className="text-left py-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                      borderTopRightRadius: '8px',
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {tablesLoading ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                      Loading…
                    </td>
                  </tr>
                ) : purchasesData.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                      No purchases yet. Sync from Xero (Settings → Integrations) or add a purchase.
                    </td>
                  </tr>
                ) : (
                purchasesData.map((purchase, index) => {
                  const statusStyle = getStatusColor(purchase.status)
                  const isLastRow = index === purchasesData.length - 1
                  return (
                    <tr key={purchase.id ?? index} style={{ borderBottom: isLastRow ? 'none' : '1px solid #E9E9EA' }}>
                      <td 
                        className="py-3"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '12px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          color: '#212126',
                          borderLeft: 'none',
                          borderRight: 'none',
                          paddingLeft: '16px',
                          ...(isLastRow && { borderBottomLeftRadius: '8px' }),
                        }}
                      >
                        {purchase.invoiceNo}
                      </td>
                      <td 
                        className="py-3"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '12px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          color: '#212126',
                          borderLeft: 'none',
                          borderRight: 'none',
                          paddingLeft: '16px',
                        }}
                      >
                        {purchase.from}
                      </td>
                      <td 
                        className="py-3"
                        style={{
                          borderLeft: 'none',
                          borderRight: 'none',
                          paddingLeft: '16px',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            color: statusStyle.textColor,
                            backgroundColor: statusStyle.bgColor,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                          }}
                        >
                          {purchase.status}
                        </span>
                      </td>
                      <td 
                        className="py-3"
                        style={{
                          borderLeft: 'none',
                          borderRight: 'none',
                          paddingLeft: '16px',
                          ...(isLastRow && { borderBottomRightRadius: '8px' }),
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <button 
                            className="rounded flex items-center justify-center"
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              backgroundColor: '#EAF1FF',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FiEye style={{ width: '16px', height: '16px', opacity: 1, color: '#2C71F6' }} />
                          </button>
                          <button 
                            className="rounded flex items-center justify-center"
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              backgroundColor: '#E8F7EF',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <img src="/images/download.svg" alt="Download" style={{ width: '16px', height: '16px' }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Client Revenue and Products and Services Row */}
      <div className="flex flex-col lg:flex-row items-stretch gap-4 sm:gap-6">
        {/* Products and Services Table - 75% width */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 flex flex-col w-full lg:w-3/4">
          <div className="flex items-center justify-between mb-4">
            <h3
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Products and Services
            </h3>
            <Link
              to={ROUTES.FINANCE_PRODUCTS_SERVICES}
              className="flex items-center justify-center"
              style={{
                width: '72px',
                height: '34px',
                borderRadius: '6px',
                border: '1px solid #2C71F6',
                padding: '8px 12px',
                backgroundColor: '#FFFFFF',
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0%',
                color: '#2C71F6',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              View all
            </Link>
          </div>
          <div className="overflow-x-auto -mx-4 sm:mx-0" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #E9E9EA' }}>
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E9E9EA' }}>
                  <th 
                    className="text-left py-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                      borderTopLeftRadius: '8px',
                    }}
                  >
                    Code
                  </th>
                  <th 
                    className="text-left py-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                    }}
                  >
                    Name
                  </th>
                  <th 
                    className="text-left py-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: '#69697A',
                      borderLeft: 'none',
                      borderRight: 'none',
                      paddingLeft: '16px',
                      borderTopRightRadius: '8px',
                    }}
                  >
                    Cost Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {tablesLoading ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                      Loading…
                    </td>
                  </tr>
                ) : productsData.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                      No products or services yet. Sync from Xero (Settings → Integrations) or add one.
                    </td>
                  </tr>
                ) : (
                productsData.map((product, index) => {
                  const isLastRow = index === productsData.length - 1
                  return (
                    <tr key={product.id ?? index} style={{ borderBottom: isLastRow ? 'none' : '1px solid #E9E9EA' }}>
                    <td 
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                        ...(isLastRow && { borderBottomLeftRadius: '8px' }),
                      }}
                    >
                      {product.code}
                    </td>
                    <td 
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                      }}
                    >
                      {product.name}
                    </td>
                    <td 
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                        ...(isLastRow && { borderBottomRightRadius: '8px' }),
                      }}
                    >
                      {product.costPrice}
                    </td>
                  </tr>
                  )
                })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Client Revenue Pie Chart - 25% width */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 flex flex-col w-full lg:w-1/4">
          <div className="flex items-center justify-between mb-4">
            <h3
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Client Revenue
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            {clientRevenueData.length > 0 ? (
              <PieChart>
                <Pie
                  data={clientRevenueData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {clientRevenueData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            ) : (
              <div className="flex items-center justify-center h-full" style={{ minHeight: 250 }}>
                <p style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A' }}>
                  No client revenue data yet
                </p>
              </div>
            )}
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {clientRevenueData.length === 0 && !overviewLoading ? (
              <p style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                No client revenue yet. Sync from Xero (Settings → Integrations) to see distribution.
              </p>
            ) : (
            clientRevenueData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: '0%',
                    color: '#69697A',
                  }}
                >
                  {item.name}
                </span>
                <span 
                  className="ml-auto"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: '0%',
                    color: '#69697A',
                  }}
                >
                  {item.value}%
                </span>
              </div>
            ))
            )}
          </div>
        </div>
      </div>

      <InvoiceDetailsModal
        isOpen={isInvoiceDetailsOpen}
        onClose={() => {
          setIsInvoiceDetailsOpen(false)
          setSelectedInvoice(null)
        }}
        invoiceData={
          selectedInvoice
            ? {
                number: selectedInvoice.number,
                ref: selectedInvoice.ref,
                document: selectedInvoice.document,
                date: selectedInvoice.date,
                dueDate: selectedInvoice.dueDate,
                paid: selectedInvoice.paid,
                due: selectedInvoice.due,
                status: selectedInvoice.status,
                xeroInvoiceId: selectedInvoice.xeroInvoiceId,
              }
            : undefined
        }
      />

      <PdfViewerModal
        isOpen={invoicePdfOpen}
        onClose={() => {
          setInvoicePdfOpen(false)
          if (invoicePdfUrl) URL.revokeObjectURL(invoicePdfUrl)
          setInvoicePdfUrl(null)
          setInvoicePdfError(null)
        }}
        pdfUrl={invoicePdfUrl}
        title={invoicePdfTitle}
        isLoading={invoicePdfLoading}
        errorMessage={invoicePdfError ?? undefined}
      />
    </div>
  )
}

export default FinanceOverview

